<template>
  <div class="container bigger-top-margin">
    <div class="row justify-content-center">
      <div class="col">
        <div v-if="error" class="alert alert-danger" style="margin-left: 5px;margin-right: 5px; margin-top: 15px;">{{ error }}</div>

        <div v-if="showWelcomeText && !state.ledger.list.length" class="card card-default">
          <div class="card-header">Nice to see you here, {{ loggedInUserName }}</div>
          <div class="card-body">
            <p class="lead">I salute you for taking an interest in your financial future, and for being willing to put some effort into it. Hat off to ya!</p>
            <p class="lead">Before I recommend you the path to follow for kickstarting your Expense Guide experience, I must warn
            you: we treat serious subjects around here with lil' bit of humour. So feel free to crack a laugh once in a
            while, it's good for your health!</p>
            Now let's get down to business — where to start?
            <ul>
              <li>In order to understand the concept of the app, you could make a quick stop to read <mark><a href="/#/support/the-story">"The Story"</a></mark>. But
              that's totally optional!</li>
              <li>If you're a person of action and can't wait to put the app to work, then start by <mark><a href="#/ledger/create">creating your first
              ledger</a></mark>, or use as a template the <mark><a href="#/support/example-ledger">example ledger</a></mark> that's already waiting for you.</li>
              <li>No idea what a ledger is? No problem — read all about it <mark><a href="#/support/ledgers">here</a></mark>, 'cause it's quite an important piece of
              the puzzle. And while you're at it, surf the rest of the <mark><a href="#/support">Help</a></mark> pages as well, you can find a lot of useful
              stuff in there.</li>
            </ul>
            I could tell you more about the app, and the steps to follow... but you're not gonna see this page once you
            create your first ledger. Feeling the pressure of remembering everything?! Haha, don't stress about it,
            that's what all the Help pages are for.<br>
            <br>
            These being said, <b>let the journey begin!</b>
          </div>
        </div>

        <div v-if="state.dashboard.unpaidExpenses" class="card card-default dashboard-upcoming">
          <div class="card-header">Unpaid expenses</div>
          <div class="card-body">
            <div v-for="(expenseList, date) in state.dashboard.unpaidExpenses" :key="date" class="expense-group">
              <div v-if="expenseList.length" class="row text-green expense-group-head">
                <div class="col-12 font-weight-bold">{{ date | formatDateShort }}</div>
              </div>

              <div class="row upcoming-one-row">
                <div v-for="(expense) in expenseList" :key="expense.id" class="col-12 upcoming-one-col">
                  <span class="upcoming-one unpaid-expense">
                    <span class="upcoming-name text-limit">{{ expense.label }}</span>
                    <a :href="'/#/ledger/' + expense.ledgerId"><b-badge variant="light" class="upcoming-ledger text-limit">{{ expense.ledgerName }}</b-badge></a>
                    <span class="upcoming-amount">{{ expense.requiredFunds | formatNumber }} {{ expense.currency | uppercase }}</span>
                    <upcoming-expense-payment
                      :key="expense.id"
                      :ledger-id="expense.ledgerId"
                      :expense-id="expense.id"
                      :expense-label="expense.label"
                      :expense-estimate="expense.requiredFunds"
                      :expense-currency="expense.currency"
                    />
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div v-if="state.dashboard.upcomingExpenses" class="card card-default dashboard-upcoming">
          <div class="card-header">Upcoming expenses</div>
          <div class="card-body">
            <div v-for="(expenseList, date) in state.dashboard.upcomingExpenses" :key="date" class="expense-group">
              <div v-if="expenseList.length" class="row text-green expense-group-head">
                <div class="col-12 font-weight-bold">{{ date | formatDateDayMonthCalendar }}, {{ date | formatDateShort }}</div>
              </div>

              <div class="row upcoming-one-row">
                <div v-for="(expense) in expenseList" :key="expense.id" class="col-12 upcoming-one-col">
                  <span class="upcoming-one">
                    <span class="upcoming-name text-limit">{{ expense.label }}</span>
                    <a :href="'/#/ledger/' + expense.ledgerId"><b-badge variant="light" class="upcoming-ledger text-limit">{{ expense.ledgerName }}</b-badge></a>
                    <span class="upcoming-amount">{{ expense.requiredFunds | formatNumber }} {{ expense.currency | uppercase }}</span>
                    <upcoming-expense-payment
                      :key="expense.id"
                      :ledger-id="expense.ledgerId"
                      :expense-id="expense.id"
                      :expense-label="expense.label"
                      :expense-estimate="expense.requiredFunds"
                      :expense-currency="expense.currency"
                    />
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div v-if="state.dashboard.ledgerStats" class="card card-default dashboard-stats">
          <div class="card-header">The numbers</div>
          <div class="card-body">
            <div v-for="(ledgerStat, ledgerId) in state.dashboard.ledgerStats" v-if="ledgerStat" :key="ledgerId" class="expense-group" style="margin-bottom: 15px;">
              <div class="row expense-group-head">
                <div class="col-12 font-weight-bold">
                  <a :href="'#/ledger/' + ledgerId" class="ledger-title-stats">
                    <i class="pe pe-book pull-right" aria-hidden="true"/> {{ ledgerStat.ledgerName }}
                  </a>
                </div>
              </div>
              <div class="row stats-card-wrap">

                <div class="col-12">
                  <div class="row stats-one-row">
                    <div class="col-6 stats-one">
                      <h5 class="stats-name">Spending</h5>
                      <span class="stats-info">(average / month)</span>
                      <b-badge class="stats-amount">{{ ledgerStat.averageMonthlySpending | formatNumber }} {{ ledgerStat.currency | uppercase }}</b-badge>
                      <hr class="stats-separator">
                      <h5 class="stats-name sm">Unexpected spending</h5>
                      <span class="stats-info sm">(average / month)</span>
                      <b-badge class="stats-amount sm">{{ ledgerStat.averageUnexpectedMonthlySpending | formatNumber }} {{ ledgerStat.currency | uppercase }}</b-badge>
                    </div>
                    <div class="col-6 stats-one">
                      <h5 class="stats-name">Future spending</h5>
                      <span class="stats-info">(average / month)</span>
                      <b-badge class="stats-amount">{{ ledgerStat.averageExpectedFutureSpending | formatNumber }} {{ ledgerStat.currency | uppercase }}</b-badge>
                    </div>
                  </div>
                </div>

                <div class="col-12">
                  <div class="row stats-one-row">
                    <div class="col-6 stats-one">
                      <h5 class="stats-name">Minimal safety net</h5>
                      <span class="stats-info">(6 months)</span>
                      <b-badge class="stats-amount">{{ minimalSafetyNetAmount(ledgerStat.averageExpectedFutureSpending,ledgerStat.averageMonthlySpending) | formatNumber }} {{ ledgerStat.currency | uppercase }}</b-badge>
                    </div>
                    <div class="col-6 stats-one">
                      <h5 class="stats-name">Optimal safety net</h5>
                      <span class="stats-info">(12 months)</span>
                      <b-badge class="stats-amount">{{ optimalSafetyNetAmount(ledgerStat.averageExpectedFutureSpending,ledgerStat.averageMonthlySpending) | formatNumber }} {{ ledgerStat.currency | uppercase }}</b-badge>
                    </div>
                  </div>
                </div>

              </div>
            </div>
            <p v-if="showStatsDescriptionText" class="lead">
              To see the statistics start adding expenses, and you will have an insight of your <mark>average monthly spending</mark>, and your <mark>future average monthly expected expenses</mark>.<br><br>
              Also you will see the <mark>minimal and optimal safety net</mark> values based on your spending.
            </p>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import UpcomingExpensePayment from './UpcomingExpensePayment'
import store from '../../store/index'
import { mapState, mapActions } from 'vuex'
let _ = require('lodash')

export default {
  name: 'Dashboard',
  components: {
    'upcomingExpensePayment': UpcomingExpensePayment
  },
  data () {
    return {
      showWelcomeText: false,
      showStatsDescriptionText: false,
      error: ''
    }
  },
  computed: {
    ...mapState({
      state: state => state
    }),
    loggedInUserName () {
      return this.state.account.me ? this.state.account.me.name : ''
    }
  },
  created () {
    let self = this
    this.fetchUnpaidExpenses()
    this.fetchUpcomingExpenses()
    this.fetchLedgerStats()
    this.pusherSetup()
    if (!store.state.app.visibilityChangeObserver.dashboard) {
      store.commit('SET_VISIBILITY_CHANGE_OBSERVER_DASHBOARD', true)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          console.log('App in background!')
        } else {
          if (self.$router.currentRoute.name === 'Dashboard') {
            self.fetchUnpaidExpenses()
            self.fetchUpcomingExpenses()
            self.fetchLedgerStats()
          }
        }
      })
    }
  },
  beforeDestroy () {
  },
  methods: {
    ...mapActions([
      'getUnpaidExpenses',
      'getUpcomingExpenses',
      'getLedgerStats'
    ]),
    fetchUnpaidExpenses () {
      this.$loading.startLoading('fetching-data-from-backend')
      this.getUnpaidExpenses()
        .then((data) => {
          this.error = ''
          this.$loading.endLoading('fetching-data-from-backend')
        })
        .catch((data) => {
          this.error = data.message
          this.$loading.endLoading('fetching-data-from-backend')
        })
    },
    fetchUpcomingExpenses () {
      this.$loading.startLoading('fetching-data-from-backend')
      this.getUpcomingExpenses()
        .then((data) => {
          this.error = ''
          this.$loading.endLoading('fetching-data-from-backend')
        })
        .catch((data) => {
          this.error = data.message
          this.$loading.endLoading('fetching-data-from-backend')
        })
    },
    fetchLedgerStats () {
      this.$loading.startLoading('fetching-data-from-backend')
      this.getLedgerStats()
        .then((data) => {
          this.error = ''
          this.$loading.endLoading('fetching-data-from-backend')

          if (!data.ledgerStats) {
            /**
             * This is a hack to prevent the display of the welcome text
             * until the data is fetched form the backend
             * @type {boolean}
             */
            this.showWelcomeText = true
          }

          let foundStatsData = false
          _.forEach(data.ledgerStats, function (item) {
            if (item) {
              foundStatsData = true
            }
          })
          if (!foundStatsData) {
            this.showStatsDescriptionText = true
          }
        })
        .catch((data) => {
          this.error = data.message
          this.$loading.endLoading('fetching-data-from-backend')
        })
    },
    pusherSetup () {
    },
    minimalSafetyNetAmount (averageExpectedFutureSpending, averageMonthlySpending) {
      if (averageExpectedFutureSpending > averageMonthlySpending) {
        return averageExpectedFutureSpending * 6
      } else {
        return averageMonthlySpending * 6
      }
    },
    optimalSafetyNetAmount (averageExpectedFutureSpending, averageMonthlySpending) {
      if (averageExpectedFutureSpending > averageMonthlySpending) {
        return averageExpectedFutureSpending * 12
      } else {
        return averageMonthlySpending * 12
      }
    }
  }
}
</script>

<style>
  .lead{
    font-size: 1rem!important;
  }
  .ledger-title-stats{
    color: rgba(0, 0, 0, 0.65);
  }
  .ledger-title-stats:hover{
    text-decoration: none;
    color: rgba(0, 0, 0, 0.65);
  }
  .ledger-title-stats .pe.pe-book {
    background-image: url(/img/svg/icon-ledger-gray.svg);
  }
  .ledger-title-stats .pe {
    width: 20px;
    height: 20px;
    margin-right: 2px;
    background-size: 18px;
    background-position: top center;
    background-repeat: no-repeat;
    vertical-align: middle;
    margin-top: 5px;
  }
  .unpaid-expense .upcoming-name{
    color: #dc3545;
  }
</style>
